import { useContext, useMemo } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'

import type { PortfolioCardProps } from '../PortfolioCard/PortfolioCard'
import PortfolioCollectionTable from '../PortfolioCollectionTable/PortfolioCollectionTable'

import styles from './PortfolioCollection.module.css'

export interface PortfolioCollectionProps {
  testID?: string
  title?: string
  portfolioItems: PortfolioCardProps[]
  variant: PortfolioCollectionVariant
  elementId?: string
  isVisibleOnMobile?: boolean
  isTableView?: boolean
}

export enum PortfolioCollectionVariant {
  Myportfolio = 'Myportfolio',
  Selling = 'Selling Enquiry',
  Watching = 'Watching',
  Buying = 'Buying Enquiry',
}

export default function PortfolioCollection({
  testID,
  title,
  portfolioItems,
  variant,
  elementId,
  isVisibleOnMobile,
  isTableView = true,
}: PortfolioCollectionProps) {
  const t = useTranslations('dashboard')
  const { stateDispatch } = useContext(GlobalStoreContext)

  const openForm = (launchForm: FormTypes, props: any) => {
    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        content: launchForm,
        props: { ...props },
      },
    })
  }

  const renderActionButton = (
    item: any,
    cta: string,
    modifierClass: string
  ) => {
    if (!item.artwork) return

    const props = {
      ...item.artwork,
      ticketId: item.ticketId,
      ticketJwt: item.ticketJwt,
      heroProps: {
        ...item.artwork.heroProps,
      },
    }

    let launchForm = undefined as any
    if (cta === 'sell') {
      launchForm = FormTypes.sell
      props['portfolioFormType'] = FormTypes.sell
    }
    if (cta === 'enquire') {
      launchForm = FormTypes.buy
      props['portfolioFormType'] = FormTypes.buy
    }

    return (
      <Button
        button={{
          type: ButtonTypes.button,
          onClick: () => openForm(launchForm, props),
        }}
        variant={ButtonVariants.primaryDark}
        className={classNames(modifierClass)}
      >
        {cta}
      </Button>
    )
  }

  const tablePortfolioItems = useMemo(
    () =>
      portfolioItems.map((item: any) => {
        return {
          ...item,
          callToAction:
            item.cta && renderActionButton(item, item.cta, styles.tableCta),
        }
      }),
    [portfolioItems]
  )

  return (
    <div
      className={classNames(styles.base, styles.offwhite)}
      data-testid={testID}
      id={elementId}
    >
      <Container>
        {title && (
          <Text
            Tag="h3"
            size={TextSizes.primaryTitleLrg}
            className={styles.title}
          >
            {title}
          </Text>
        )}
      </Container>
      <PortfolioCollectionTable
        items={tablePortfolioItems}
        variant={variant}
        isTableView={isTableView}
      />
      {(variant === PortfolioCollectionVariant.Selling ||
        variant === PortfolioCollectionVariant.Buying) && (
        <div className={styles.buttonContainer}>
          <Button
            variant={ButtonVariants.secondaryDark}
            iconLeft={true}
            link="/artworks"
          >
            {t('browseAndAddArtwork')}
          </Button>
        </div>
      )}
    </div>
  )
}
